import { useState, useEffect } from 'react';
import authService from '../services/authService';

/**
 * Hook to get current user permissions for shipment types
 * This can be easily connected to your actual user service/context
 * 
 * To test different permission scenarios, you can set permissions in browser console:
 * 
 * // User can only add Freight:
 * localStorage.setItem('userShipmentPermissions', JSON.stringify({ canAddFreight: true, canAddVehicles: false }));
 * 
 * // User can only add Vehicles:
 * localStorage.setItem('userShipmentPermissions', JSON.stringify({ canAddFreight: false, canAddVehicles: true }));
 * 
 * // User can add both (default):
 * localStorage.setItem('userShipmentPermissions', JSON.stringify({ canAddFreight: true, canAddVehicles: true }));
 * 
 * Then refresh the page to see the changes.
 * 
 * @returns {Object} User permissions object with canAddFreight and canAddVehicles
 */
export const useUserPermissions = () => {
  const [permissions, setPermissions] = useState({
    canAddFreight: true,  // Default: allow both (for users who can add both)
    canAddVehicles: true,
    loading: true
  });

  useEffect(() => {
    // TODO: Replace this with actual user permission logic
    // This is a placeholder that can be connected to:
    // 1. User context/state management
    // 2. API call to get user profile with permissions
    // 3. localStorage/sessionStorage with user data
    
    const fetchUserPermissions = async () => {
      try {
        // Example: Get user profile from API
        // const profile = await authService.getProfile();
        // const userRole = profile.role;
        // const userPermissions = profile.permissions;
        
        // Mock implementation - replace with actual logic
        // Example permission logic based on role:
        // const canAddFreight = ['admin', 'dispatcher', 'manager'].includes(userRole) || userPermissions?.canAddFreight;
        // const canAddVehicles = ['admin', 'dispatcher', 'manager'].includes(userRole) || userPermissions?.canAddVehicles;
        
        // For now, using localStorage as a simple way to set permissions
        // You can set this from your login/auth flow
        const savedPermissions = localStorage.getItem('userShipmentPermissions');
        
        if (savedPermissions) {
          const parsed = JSON.parse(savedPermissions);
          setPermissions({
            canAddFreight: parsed.canAddFreight ?? true,
            canAddVehicles: parsed.canAddVehicles ?? true,
            loading: false
          });
        } else {
          // Default: allow both types (for users who can add both)
          // You can change this default based on your requirements
          setPermissions({
            canAddFreight: true,
            canAddVehicles: true,
            loading: false
          });
        }
      } catch (error) {
        console.error('Error fetching user permissions:', error);
        // Default to allowing both on error
        setPermissions({
          canAddFreight: true,
          canAddVehicles: true,
          loading: false
        });
      }
    };

    fetchUserPermissions();
  }, []);

  return permissions;
};

export default useUserPermissions;

